<template>
  <SectionTemplate title="常见问题（FAQs）">
    <XFaq :faq-list="faqList" @contact="handleContact" />
  </SectionTemplate>
</template>

<script setup lang="ts">
import XFaq from '@/design/widgets/faq/faq.vue';
import { useFaqHelper } from '@/design/widgets/faq/useFaqHelper';
import { showDynamicContact } from '@rendora/common/features/contact';
import SectionTemplate from '~/design/widgets/landing-section/cn-template.vue';

const linksConfig = {
  'ai-voice': 'https://www.youyan3d.com/workspace/voice',
  lab: 'https://www.youyan3d.com/workspace/lab',
  '3d-avatar-library': 'https://www.youyan3d.com/characters',
  'ai-ppt': 'https://www.youyan3d.com/editor/page/ppt',
};

// 将链接配置转换为 FaqLink 格式
const links = Object.entries(linksConfig).map(([key, url]) => ({ key, url }));

// 使用 FAQ 辅助工具
const { getLink, getStrong, getContactLink } = useFaqHelper(links);
// todo answer文案没有换
// FAQ 数据列表
const faqList = [
  {
    question: '有言PPT Speak是什么？它解决了什么核心问题？',
    answer: `有言PPT Speak是一款革命性的AI互动内容工具。它不仅仅是将PPT转为视频，更是赋予您PPT“生命”与“智慧”。通过上传PPT，AI会深度解析内容，构建一个专属的、可被查询的知识库，并生成一个能够进行实时讲解和智能答疑的数字人形象。
    它解决的核心问题是：${getStrong('传统 PPT 或数字人讲解视频只能单向输出，无法应对真实用户的个性化提问')}。PPT Speak让观众从“被动观看者”转变为“主动提问者”，通过与数字人的对话，深度探索和理解PPT中的每一个知识点。这彻底改变了信息单向传递的模式，实现了知识的双向互动与主动吸收，特别适合教学、培训、销售、咨询等高频问答场景。`,
    isOpen: false,
  },
  {
    question: '有言PPT Speak和普通PPT播放、数字人讲解视频有什么区别？',
    answer: `普通PPT或PPT讲解视频，本质是内容展示工具，信息流向是单向的，用户只能被动接收。他们回答不了“这个点我没听懂怎么办”。
PPT Speak的本质是一个${getStrong('基于PPT构建的知识型智能体')}，用户可以随时提问，系统会从 PPT 知识库中提取相关页面信息进行回答。当内容存在理解门槛、用户问题高度分散时，PPT Speak能显著降低沟通成本，而传统PPT更适合只做“展示”。
简单理解：${getStrong('视频是演讲，PPT Speak 是专家坐在你面前随时解答')}。`,
    isOpen: false,
  },
  {
    question: '有言PPT Speak是如何理解并使用PPT内容的？',
    answer: `PPT Speak会对PPT每一页的文字注释、逻辑结构和信息重点进行解析，自动构建一个结构化知识库。数字人所有回答都基于这个知识库生成，而不是凭空生成内容。当用户提问时，问题会先被AI理解，然后在这个封闭的知识库中进行语义检索和匹配，确保回答严格源自您的原始材料。
与普通AI聊天机器人相比，PPT Speak的回答范围更可控、更专业，也更适合对外使用和规模化传播。`,
    isOpen: false,
  },
  {
    question: '这个产品主要服务于哪些人群？',
    answer: [
      'PPT Speak服务于所有需要高效、生动传递复杂信息，并渴望获得反馈与互动的角色。核心用户包括：',
      [
        '教师、培训师、讲师，用于创建可24小时课后答疑的AI助教。',
        '销售、市场、产品经理，用于打造永不疲倦的智能产品导购和方案讲解员。',
        '政务、医疗、法律、金融从业者，用于制作可自助咨询的政策解读员、健康顾问和法律助手，进行基础咨询分流和精准信息解读。',
        '企业HR、内部培训部门，用于建立标准化的制度解读与员工培训智能助手。',
      ],
      `其本质是任何拥有结构化文档（PPT）并希望它“活起来”与人对话的个人或组织。如果你的内容${getStrong('问题多、重复问答频繁')}，PPT Speak 的价值会远高于单纯的视频形式。`,
    ],
    isOpen: false,
    isSlot: true,
  },
  {
    question: '我需要具备编程或AI知识才能使用PPT Speak吗？',
    answer: `完全不需要，这是我们的核心设计原则。 使用PPT Speak的门槛比制作PPT本身还要低。整个过程像使用一个智能的“PPT升级向导”，您只需：
（1）上传PPT文件，AI会自动提取信息，解析成对应的知识库。
（2）在丰富的素材库中选择一个喜欢的数字人形象和声音。
（3）点击发布。
所有复杂的AI知识库构建、自然语言处理、数字人驱动技术都由平台在后台自动完成。您只需专注于准备优质的内容（PPT），几分钟内就能获得一个专业的、可交互的智能成果。`,
    isOpen: false,
  },
  {
    question: '浏览模式和问答模式分别适合什么需求？',
    answer: `这是PPT Speak设计的两种核心交互模式，模拟了真实的学习和交流场景。
浏览模式：相当于“传统课堂听讲”。数字人按PPT顺序进行系统、完整的讲解，适合初次接触内容的观众，或希望系统化学习的场景。
问答模式：相当于“课后一对一答疑”。观众在任何时候（包括浏览中途）都可以随时暂停，通过语音或文字提出个性化问题。数字人会中断当前播放，直接调取知识库中最相关的片段进行针对性解答。
两者无缝切换，满足从“系统输入”到“疑点攻克”的全流程学习需求。对比单纯的讲解视频，视频只有“浏览模式”，观众遇到疑问只能倒回去重复听，无法获得个性化解答。PPT Speak则完美结合了两者，效率更高。`,
    isOpen: false,
  },
  {
    question: '我可以定制数字人的形象和声音吗？',
    answer: `当然可以，并且我们提供了极其丰富的选择来匹配不同场景。平台拥有${getLink('3d-avatar-library', '数千款数字人形象')}，涵盖不同国籍、年龄、职业风格（如商务精英、亲切教师、科技感形象等）。同时，提供数${getLink('ai-voice', '百种AI音色')}，包括多种语言、方言以及不同情感色彩的语调。您可以根据内容性质自由搭配：例如，严肃的金融报告搭配专业干练的形象和沉稳音色；儿童教育内容则搭配活泼可爱的形象和亲切音色。这种高度定制化能极大增强观众的代入感和信任感。
我们也提供自定义功能，您可以使用${getLink('lab', '人物克隆和声音克隆')}，将您的形象复刻到平台中使用，同时也可以手动捏脸调整面部特征和服装搭配等，完全满足您的个性化需求。`,
    isOpen: false,
  },
  {
    question: '除了上传PPT，我可以用AI直接生成PPT再创建智能体吗？',
    answer: `完全可以，这体现了有言产品矩阵的协同优势。您可以使用我们独立的 “${getLink('ai-ppt', 'AIPPT')}”功能：只需输入一个主题或一段文案，AI就能在几分钟内生成结构清晰、图文并茂的完整PPT。这个由AI生成的PPT，可以直接作为PPT Speak的输入源，一键跳转进入创建智能体的流程。这实现了一条完整的“从无到有”的自动化生产线：从创意→文案→PPT→可交互数字人智能体，极大解放了内容创作生产力，尤其适合需要快速产出内容的场景。`,
    isOpen: false,
  },
  {
    question: '与市场上其他数字人产品相比，你们的独特优势是什么？',
    answer: `我们的定位非常聚焦：“深度文档交互”。许多数字人产品侧重于直播带货、虚拟客服或形象播报。PPT Speak的独特优势在于：
1. 与PPT/文档的深度绑定：AI不是简单播报，而是深度解析并构建可检索的知识库，实现问答时对原文的精准引用。
2. “讲解+答疑”双模式设计：无缝融合了系统化学习与个性化探索，符合深度知识传递的认知规律。
3. 场景化解决方案：我们不仅提供工具，更针对教育、企业培训、政务等场景提供了深度的应用模板和价值思考。如果您的主要需求是让一份厚重的文档“活起来”并与受众深度互动，PPT Speak是最专业、最对口的解决方案。`,
    isOpen: false,
  },
  {
    question: 'PPT Speak是否支持大规模用户同时访问？',
    answer: `支持万人级并发访问，不用担心并发压力。平台可稳定支持超万人同时在线互动。无论是企业内部全员培训、学校全校公开课，还是大型产品发布会现场，所有用户都能获得流畅的讲解、提问和回答体验，无延迟、无卡顿。这种高并发能力是单纯部署在本地或算力不足的平台难以实现的，保障了大型需求场景下的稳定服务。
您也可以联系我们的产品技术专家，${getContactLink('contact', '获取定制方案服务')}。`,
    isOpen: false,
  },
  {
    question: '使用 PPT Speak 是否需要用户下载 App？',
    answer: `不需要，这是关键优势之一，跨平台、零门槛访问。 您创建的每一个PPT Speak智能体，都会自动生成一个独立的链接和一个二维码。观众使用任何智能设备（手机、平板、电脑）的浏览器，扫描二维码或点击链接，即可直接打开交互页面，无需下载、安装任何应用程序，也无需注册登录。这种极简的访问方式，最大程度地降低了用户的体验阻力，方便大规模传播和快速部署。`,
    isOpen: false,
  },
  {
    question: '后台能看到观众的使用数据吗？能看到什么？',
    answer: [
      '可以，我们提供多维度的数据看板，让内容创建者“看见”互动效果。您不仅能看到基础的访问人数、时长，更能看到深度的行为数据：',
      [
        '热点内容：哪些PPT页面被反复观看和提问？',
        '高频问题：观众最常问的是什么？这反映了知识的难点或兴趣点。',
        '个体轨迹：每个观众看了多久，问了哪些问题？（在合规前提下）。',
        '问答日志：所有问答记录的完整存档。',
      ],
      `这些数据对于优化PPT内容、改进教学重点、洞察客户需求、评估宣传效果具有极高的价值，这是单向视频无法提供的洞察力。`,
    ],
    isOpen: false,
    isSlot: true,
  },
  {
    question: '在教育领域，PPT Speak如何帮助老师和学生？',
    answer: `对老师而言，它是超级助教。上传课件后，即创建一个永不疲倦的“AI教师分身”，能7x24小时解答学生海量、重复的基础性问题，让老师从机械性答疑中解放，专注于教学设计和个性化指导。对学生而言，它是个人家教。课后扫码，不仅能复习课程，更能针对自己的薄弱点随时提问，获得即时、准确的解答，学习路径从“统一教学”变为“按需学习”。后台数据还能让老师清晰看到全班学生的疑点分布，实现精准教学。这与单纯观看讲课视频相比，实现了从“统一灌输”到“个性化吸收”的质变。
`,
    isOpen: false,
  },
  {
    question: '对于企业销售，这个工具有什么实际好处？',
    answer: `它将复杂的销售资料（产品PPT、方案书）转化为一个可随身携带的“智能销售专家”。销售人员无需死记硬背所有细节，只需将智能体二维码分享给客户。客户扫码后，既能系统了解产品，又能随时深入询问自己最关心的差异化优势、技术参数或报价细节，数字人都会精准演示并解答。这带来了三大价值：一是解放销售精力，使其聚焦于关系维护和谈判；二是提升客户体验，提供随时随地的专业咨询服务；三是统一输出口径，确保信息传递准确无误。相比仅发送一个讲解视频，互动性极大地促进了客户参与度和转化意愿。
您可以联系我们的行业专家，${getContactLink('contact', '获取定制解决方案')}。
`,
    isOpen: false,
  },
  {
    question: '医疗健康场景下，它能解决什么问题？',
    answer: `它充当了医生与患者之间的高效“桥梁”。医生可将标准的疾病康复指南、术后注意事项等宣教PPT，转化为一位权威、有耐心的“AI康复顾问”。患者出院后，在家扫码即可随时询问“伤口如何护理”、“药物何时吃”、“什么能吃什么不能吃”等常见问题，获得基于医疗指南的规范回答。这能有效分流医生大量的常规随访和咨询压力，减少患者因遗忘或误解导致的康复风险，提升患者管理效率和依从性。它比发放静态的纸质或视频资料，提供了动态的答疑支持。
您可以联系我们的行业专家，${getContactLink('contact', '获取定制解决方案')}。
`,
    isOpen: false,
  },
];

// 处理联系方式点击
const handleContact = (action: string) => {
  if (action === 'contact') {
    showDynamicContact(1078924);
  }
};
</script>
